import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "@/context/ThemeContext";
import { usePageContent, getSection } from "@/hooks/usePageContent";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import SEO from "@/components/SEO";
import { 
  ArrowRight, 
  Clock,
  User,
  Search,
  ChevronRight,
  BookOpen,
  TrendingUp,
  Calendar,
  Tag,
  Eye,
  Sparkles,
  Loader2
} from "lucide-react";

const API_URL = window.location.origin;

// Default fallback image for blog posts without images
const DEFAULT_BLOG_IMAGE = 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/ff7136f51a33cfcbce14d4090c85bdaaf22de77c4467c8bb6ae9b571998687dd.png';

// Fallback static blog posts (used when API has no data)
const staticBlogPosts = [
  {
    id: 1,
    title: 'Complete Guide to US Student Visa (F1) in 2025',
    slug: 'us-student-visa-f1-guide-2025',
    excerpt: 'Everything you need to know about applying for a US F1 student visa - from DS-160 to visa interview preparation.',
    featured_image: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/04ca0c7559d8b0ef20b6b0d8ba1bca43c59bb4e9f2c69a6c9fb5035bb27a50fd.png',
    category: 'Visa Guide',
    author_name: 'Dr. Priya Mehta',
    publish_date: '2025-01-05',
    tags: ['USA', 'F1 Visa', 'Student Visa'],
    is_featured: true,
    views: 15420
  },
  {
    id: 2,
    title: 'Top 10 Scholarships for Indian Students in 2025',
    slug: 'top-scholarships-indian-students-2025',
    excerpt: 'Discover the best scholarship opportunities available for Indian students studying abroad.',
    featured_image: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/f4718d2348b6a2dc56e506bd674c84d2d557a075fcfa0afd4029a3e8f3422f4a.png',
    category: 'Scholarships',
    author_name: 'Rahul Sharma',
    publish_date: '2025-01-03',
    tags: ['Scholarships', 'Financial Aid'],
    is_featured: true,
    views: 12350
  },
  {
    id: 3,
    title: 'How to Write a Winning Statement of Purpose (SOP)',
    slug: 'how-to-write-winning-sop',
    excerpt: 'Step-by-step guide to crafting an SOP that gets you admitted to your dream university.',
    featured_image: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/10fcff5fa9d1e829d02440a3b6d42e095cf02c7beec7fa325f5f40cb2399400d.png',
    category: 'Application Tips',
    author_name: 'Anjali Desai',
    publish_date: '2024-12-28',
    tags: ['SOP', 'Application'],
    is_featured: false,
    views: 9870
  },
  {
    id: 4,
    title: 'Canada vs Australia: Which is Better for Indian Students?',
    slug: 'canada-vs-australia-indian-students',
    excerpt: 'A comprehensive comparison of studying in Canada vs Australia - costs, work opportunities, and PR pathways.',
    featured_image: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/ff7136f51a33cfcbce14d4090c85bdaaf22de77c4467c8bb6ae9b571998687dd.png',
    category: 'Destination Guide',
    author_name: 'Vikram Singh',
    publish_date: '2024-12-25',
    tags: ['Canada', 'Australia', 'Comparison'],
    is_featured: false,
    views: 8540
  },
  {
    id: 5,
    title: 'GRE vs GMAT: Which Test Should You Take?',
    slug: 'gre-vs-gmat-comparison',
    excerpt: 'Understand the key differences between GRE and GMAT to make the right choice for your MBA applications.',
    featured_image: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/10fcff5fa9d1e829d02440a3b6d42e095cf02c7beec7fa325f5f40cb2399400d.png',
    category: 'Test Prep',
    author_name: 'Dr. Priya Mehta',
    publish_date: '2024-12-20',
    tags: ['GRE', 'GMAT', 'MBA'],
    is_featured: false,
    views: 7230
  },
  {
    id: 6,
    title: 'Part-Time Job Guide for International Students in UK',
    slug: 'part-time-jobs-uk-students',
    excerpt: 'Complete guide to finding part-time work while studying in the UK - rules, opportunities, and tips.',
    featured_image: 'https://static.prod-images.emergentagent.com/jobs/26742231-43d0-4f28-a4c7-d30aa20ab27f/images/70fc8fe155890b004cee7196ca11daa136f0e8c226cdc05a5a98a02c60274689.png',
    category: 'Student Life',
    author_name: 'Rahul Sharma',
    publish_date: '2024-12-15',
    tags: ['UK', 'Part-time Jobs', 'Student Life'],
    is_featured: false,
    views: 6890
  },
];

const staticCategories = ['All', 'Visa Guide', 'Scholarships', 'Application Tips', 'Destination Guide', 'Test Prep', 'Student Life'];

export const BlogPage = () => {
  const { isDark } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [blogPosts, setBlogPosts] = useState([]);
  const [categories, setCategories] = useState(staticCategories);
  const [loading, setLoading] = useState(true);
  const [usingFallback, setUsingFallback] = useState(false);

  // CMS Page Content (Phase D wiring)
  const { content: cmsContent } = usePageContent('blog-list');
  const heroContent = getSection(cmsContent, 'hero', {
    badge: 'Study Abroad Blog',
    title: 'Insights &',
    title_highlight: 'Guides',
    subtitle: 'Expert advice, tips, and resources to help you navigate your study abroad journey.',
    search_placeholder: 'Search articles...'
  });
  const featuredHeader = getSection(cmsContent, 'featured_header', {
    title: 'Featured Articles'
  });
  const trendingHeader = getSection(cmsContent, 'trending_header', {
    title: 'Trending Now'
  });
  const newsletterCta = getSection(cmsContent, 'newsletter_cta', {
    title: 'Stay Updated',
    subtitle: 'Get the latest study abroad tips delivered to your inbox.',
    email_placeholder: 'Enter your email',
    subscribe_label: 'Subscribe'
  });
  const tagsCard = getSection(cmsContent, 'tags_card', {
    title: 'Popular Tags',
    tags: ['USA', 'UK', 'Canada', 'Scholarships', 'Visa', 'IELTS', 'SOP', 'MBA', 'Engineering']
  });

  // Fetch blog posts from API
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const res = await fetch(`${API_URL}/api/public/blog?limit=50`);
        if (res.ok) {
          const data = await res.json();
          if (data.posts && data.posts.length > 0) {
            setBlogPosts(data.posts);
            if (data.categories && data.categories.length > 0) {
              setCategories(['All', ...data.categories]);
            }
            setUsingFallback(false);
          } else {
            // Use static fallback if no published posts
            setBlogPosts(staticBlogPosts);
            setUsingFallback(true);
          }
        } else {
          setBlogPosts(staticBlogPosts);
          setUsingFallback(true);
        }
      } catch (e) {
        console.error('Failed to fetch blog posts:', e);
        setBlogPosts(staticBlogPosts);
        setUsingFallback(true);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  const featuredPosts = blogPosts.filter(post => post.is_featured || post.isFeatured);
  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          (post.excerpt || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = activeCategory === 'All' || post.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  const trendingPosts = [...blogPosts].sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, 4);

  if (loading) {
    return (
      <div className={`min-h-screen pt-20 flex items-center justify-center ${isDark ? 'bg-[#1a2830]' : 'bg-white'}`}>
        <Loader2 className="w-8 h-8 animate-spin text-ux-cyan" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen pt-20 ${isDark ? 'bg-[#1a2830]' : 'bg-white'}`} data-testid="blog-page">
      {/* SEO Meta Tags */}
      <SEO 
        title="Study Abroad Blog - Tips, Guides & Expert Advice"
        description="Read expert study abroad tips, visa guides, scholarship information, and university admission advice. Latest articles on studying in USA, UK, Canada, Australia from India."
        keywords="study abroad blog, study abroad tips, USA visa guide, UK university admission, Canada student visa, Australia scholarship, IELTS tips, SOP writing, study abroad from India, overseas education blog"
        canonical="https://unixperts.com/blog"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Blog",
          "name": "UniXperts Study Abroad Blog",
          "description": "Expert advice and guides for Indian students planning to study abroad",
          "url": "https://unixperts.com/blog",
          "publisher": {
            "@type": "Organization",
            "name": "UniXperts",
            "logo": {
              "@type": "ImageObject",
              "url": "https://unixperts.com/logo512.png"
            }
          },
          "blogPost": blogPosts.slice(0, 5).map(post => ({
            "@type": "BlogPosting",
            "headline": post.title,
            "description": post.excerpt,
            "image": post.featured_image,
            "author": {
              "@type": "Person",
              "name": post.author_name
            },
            "datePublished": post.publish_date,
            "url": `https://unixperts.com/blog/${post.slug}`
          }))
        }}
      />
      
      {/* Hero Section */}
      <section className={`py-20 md:py-28 relative overflow-hidden ${isDark ? 'bg-gradient-to-br from-[#1a2830] via-[#233842] to-[#1a2830]' : 'bg-gradient-to-br from-ux-charcoal via-ux-charcoal to-ux-violet-dark'}`}>
        <div className="absolute top-0 right-0 w-96 h-96 bg-ux-cyan/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-ux-violet/20 rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <Badge className="bg-ux-cyan/20 text-ux-cyan border-ux-cyan/30 mb-6">
              <BookOpen className="w-3 h-3 mr-1" /> {heroContent.badge}
            </Badge>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 text-white tracking-tight" data-testid="blog-hero-title">
              {heroContent.title} <span className="gradient-text-aurora">{heroContent.title_highlight}</span>
            </h1>
            <p className="text-lg md:text-xl text-white/80 mb-8">
              {heroContent.subtitle}
            </p>
            
            {/* Search Bar */}
            <div className="max-w-xl mx-auto relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <Input 
                placeholder={heroContent.search_placeholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 py-6 bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-full text-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Featured Posts */}
      {featuredPosts.length > 0 && (
        <section className={`py-16 ${isDark ? 'bg-[#233842]' : 'bg-ux-slate-light'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                <Sparkles className="inline w-5 h-5 mr-2 text-ux-amber" />
                {featuredHeader.title}
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              {featuredPosts.map((post) => (
                <Link key={post.id} to={`/blog/${post.slug}`} className="group">
                  <div className="glass-panel rounded-3xl overflow-hidden hover:scale-[1.02] transition-all">
                    <div className="relative h-64">
                      <img 
                        src={post.featured_image || post.image || DEFAULT_BLOG_IMAGE} 
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        onError={(e) => { e.target.src = DEFAULT_BLOG_IMAGE; }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                      <Badge className="absolute top-4 left-4 bg-ux-cyan text-white">
                        {post.category}
                      </Badge>
                      <div className="absolute bottom-4 left-4 right-4">
                        <h3 className="text-xl font-bold text-white mb-2 group-hover:text-ux-cyan transition-colors">
                          {post.title}
                        </h3>
                        <div className="flex items-center gap-4 text-white/70 text-sm">
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" /> {post.readTime || '5 min'}
                          </span>
                          <span className="flex items-center gap-1">
                            <Eye className="w-4 h-4" /> {(post.views || 0).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Categories */}
      <section className={`py-8 border-b ${isDark ? 'bg-[#1a2830] border-white/10' : 'bg-white border-ux-charcoal/10'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-2 overflow-x-auto pb-2 hide-scrollbar">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  activeCategory === category
                    ? 'bg-gradient-to-r from-ux-cyan to-ux-blue text-white'
                    : isDark 
                      ? 'bg-white/5 text-white/70 hover:bg-white/10' 
                      : 'bg-ux-slate-light text-ux-charcoal/70 hover:bg-ux-charcoal/10'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className={`py-16 ${isDark ? 'bg-[#1a2830]' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Blog Posts */}
            <div className="lg:col-span-2">
              <div className="space-y-8">
                {filteredPosts.map((post) => (
                  <Link key={post.id} to={`/blog/${post.slug}`} className="group block">
                    <div className={`glass-panel rounded-3xl overflow-hidden hover:scale-[1.01] transition-all`}>
                      <div className="grid md:grid-cols-5 gap-0">
                        <div className="md:col-span-2 relative h-48 md:h-full">
                          <img 
                            src={post.featured_image || post.image || DEFAULT_BLOG_IMAGE} 
                            alt={post.title}
                            className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                            onError={(e) => { e.target.src = DEFAULT_BLOG_IMAGE; }}
                          />
                        </div>
                        <div className="md:col-span-3 p-6">
                          <Badge className={`mb-3 ${isDark ? 'bg-ux-violet/20 text-ux-violet' : 'bg-ux-violet/10 text-ux-violet'}`}>
                            {post.category}
                          </Badge>
                          <h3 className={`text-xl font-bold mb-2 group-hover:text-ux-cyan transition-colors ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                            {post.title}
                          </h3>
                          <p className={`mb-4 line-clamp-2 ${isDark ? 'text-white/60' : 'text-ux-charcoal/60'}`}>
                            {post.excerpt}
                          </p>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-ux-violet flex items-center justify-center text-white text-sm font-bold">
                                {(post.author_name || post.author || 'U')[0]}
                              </div>
                              <div>
                                <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>{post.author_name || post.author}</p>
                                <p className={`text-xs ${isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>{post.publish_date || post.date}</p>
                              </div>
                            </div>
                            <div className={`flex items-center gap-3 text-sm ${isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" /> {post.readTime || '5 min'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-8">
              {/* Trending Posts */}
              <div className={`glass-panel rounded-3xl p-6`}>
                <h3 className={`text-lg font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                  <TrendingUp className="w-5 h-5 text-ux-coral" />
                  {trendingHeader.title}
                </h3>
                <div className="space-y-4">
                  {trendingPosts.map((post, index) => (
                    <Link key={post.id} to={`/blog/${post.slug}`} className="flex gap-3 group">
                      <span className={`text-2xl font-bold ${isDark ? 'text-white/20' : 'text-ux-charcoal/20'}`}>
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <div>
                        <h4 className={`font-medium group-hover:text-ux-cyan transition-colors line-clamp-2 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                          {post.title}
                        </h4>
                        <p className={`text-xs ${isDark ? 'text-white/50' : 'text-ux-charcoal/50'}`}>
                          {(post.views || 0).toLocaleString()} views
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Newsletter */}
              <div className="rounded-3xl p-6 bg-gradient-to-br from-ux-cyan to-ux-violet text-white">
                <h3 className="text-lg font-bold mb-2">{newsletterCta.title}</h3>
                <p className="text-white/80 text-sm mb-4">
                  {newsletterCta.subtitle}
                </p>
                <Input 
                  placeholder={newsletterCta.email_placeholder}
                  className="bg-white/20 border-white/30 text-white placeholder:text-white/50 mb-3"
                />
                <Button className="w-full bg-white text-ux-blue hover:bg-white/90 font-medium">
                  {newsletterCta.subscribe_label}
                </Button>
              </div>

              {/* Tags */}
              <div className={`glass-panel rounded-3xl p-6`}>
                <h3 className={`text-lg font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-ux-charcoal'}`}>
                  <Tag className="w-5 h-5 text-ux-amber" />
                  {tagsCard.title}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {(tagsCard.tags || []).map((tag) => (
                    <Badge 
                      key={tag} 
                      variant="outline" 
                      className={`cursor-pointer hover:bg-ux-cyan hover:text-white hover:border-ux-cyan transition-colors ${isDark ? 'border-white/20 text-white/70' : ''}`}
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
